import { useEffect, useLayoutEffect, useState, useRef, Suspense, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RouterProvider, createBrowserRouter } from 'react-router-dom';
import { registerApplication, start, addErrorHandler, getAppNames, unregisterApplication, navigateToUrl } from 'single-spa';
import { SdfBusyIndicator } from '@waypoint/react-components';
import { useAuthentication } from '../hooks/useAuthentication';
import { CONFIG } from '../utils/config';
import { SharedHeader } from 'src/components/SharedHeader';
import { SharedFooter } from 'src/components/SharedFooter';
import { LoginPageComponent } from '../components/LoginPageComponent';
import ContactUs from '../components/remotes/contactApp';
import { LogoutPageComponent } from '../components/LogoutPageComponent';
import { MuiThemeProvider } from '../components/MuiThemeProvider';
import { getUserContext, setCountryAndLocale, store } from '@headless-shell/store';
import { loadPendoTracking, unloadPendoTracking } from '../analytics/pendo';
import { APP_ROUTES } from '../constants';
import { loadInteractionStudio } from '../analytics/externalTracking';
import { loadDemandBase } from '../analytics/demandBase';
import i18n from '../../i18n';
// replaced local store imports with shared package

import {
  RootContainer,
  LoadingState,
  LoadingContent,
  ErrorState,
  ErrorContent,
  ErrorTitle,
  ErrorMessage,
  StyledReloadButton,
  MainContainer,
  AppContent
} from './styles';
import { toShellRelativePath, getActiveRoutes, SHELL_BASE_PATH, updatePageMeta, countryNavigateUrl } from '../utils/helper';
// Global flag to track if Single-SPA has been started
let singleSpaStartedGlobally = false;
// appId captured from app-details (/apps/:id) when checkout is triggered
// let pendingCheckoutAppId = null;

const ABSOLUTE_HTTP_URL_REGEX = /^https?:\/\//i;
const LEGACY_LOGIN_PATH_REGEX = /^\/login(?:\/|$)/;
const LEGACY_LOGOUT_PATH_REGEX = /^\/logout(?:\/|$)/;
const LOCALE_PREFIX_REGEX = /^\/[a-z]{2}(?:-[a-z]{2})?(?=\/|$)/i;

const trimTrailingSlashes = (value = '') => value.replace(/\/+$/, '');
const ROUTER_BASENAME = (() => {
  const explicitBasePath = trimTrailingSlashes(process.env.REACT_APP_SHELL_BASE_PATH || '');
  if (explicitBasePath) return explicitBasePath;

  const inferredBasePath = trimTrailingSlashes(SHELL_BASE_PATH || '');
  // SHELL_BASE_PATH may currently be derived from REACT_APP_SHELL_URL.
  // If that URL points at the static asset prefix, it is NOT a router basename.
  return inferredBasePath.startsWith('/static/') ? '' : inferredBasePath;
})();

const normalizePath = (value = '/') => `${value || '/'}`.replace(/\/{2,}/g, '/');
const removeLeadingSlashes = (value = '') => String(value).replace(/^\/+/, '');
const stripLocalePrefix = (pathname = '/') => {
  const normalized = normalizePath(pathname);
  const withoutLocale = normalized.replace(LOCALE_PREFIX_REGEX, '') || '/';
  return withoutLocale.startsWith('/') ? withoutLocale : `/${withoutLocale}`;
};
const normalizeRoutePath = (value = '/') => trimTrailingSlashes(normalizePath(value)) || '/';

// Checkout CTA context is persisted in sessionStorage so it survives AppShell
// remounts (component refs reset) and the URL strip (query removed).
const CHECKOUT_CTX_STORAGE_KEY = 'cmp.checkoutContext';
const persistCheckoutContext = (ctx) => {
  try { window.sessionStorage.setItem(CHECKOUT_CTX_STORAGE_KEY, JSON.stringify(ctx)); } catch { /* ignore */ }
};
const readPersistedCheckoutContext = () => {
  try {
    const raw = window.sessionStorage.getItem(CHECKOUT_CTX_STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
};
// Resolve the checkout appId (productId) for the checkout MFE.
// Priority: explicit ref set by app-details Buy Now → productId query param
// (legacy storefront CTA redirect to /checkout/cart?productId=...) → numeric
// /checkout/:appId path segment. The query-param fallback preserves the app
// context on a full-page load from the CTA endpoint, where the ref is empty.
const resolveCheckoutAppId = (checkoutAppIdRef) => {
  if (checkoutAppIdRef?.current) return checkoutAppIdRef.current;
  try {
    const productId = new URLSearchParams(window.location.search).get('productId');
    if (productId) return productId;
    const path = stripLocalePrefix(toShellRelativePath(window.location.pathname));
    const match = path.match(/^\/checkout\/(\d+)(?:\/|$)/);
    if (match) return match[1];
  } catch (e) {
    console.error('[AppShell] resolveCheckoutAppId error', e);
  }
  return readPersistedCheckoutContext()?.appId || null;
};
// Resolve the checkout editionId for the checkout MFE.
// Priority: ref captured before the URL was cleaned → editionId query param
// (legacy CTA redirect). Selects a specific edition/plan; when absent the
// checkout MFE defaults to the first edition.
// CTA context forwarded from the legacy storefront redirect
// (/checkout/cart?editionId=...&type=FREE_TRIAL&canRouteToV2=true&shoppingCart=true).
// Prefer values captured in the ref before the URL was cleaned (see
// captureAndStripCheckoutParams) so they survive the query-string removal.
const resolveCheckoutCtaFlags = (checkoutCtaFlagsRef) => {
  if (checkoutCtaFlagsRef?.current) return checkoutCtaFlagsRef.current;
  try {
    const params = new URLSearchParams(window.location.search);
    const hasParams = params.has('editionId') || params.has('type')
      || params.has('canRouteToV2') || params.has('shoppingCart');
    if (hasParams) {
      return {
        editionId: params.get('editionId') || null,
        ctaType: params.get('type') || null,
        canRouteToV2: params.get('canRouteToV2') === 'true',
        shoppingCart: params.get('shoppingCart') === 'true',
      };
    }
  } catch (e) {
    console.error('[AppShell] resolveCheckoutCtaFlags error', e);
  }
  const persisted = readPersistedCheckoutContext();
  return {
    editionId: persisted?.editionId || null,
    ctaType: persisted?.ctaType || null,
    canRouteToV2: persisted?.canRouteToV2 || false,
    shoppingCart: persisted?.shoppingCart || false,
  };
};

// Capture the checkout context from the legacy CTA query params into in-memory
// refs, then strip them from the address bar via history.replaceState. This
// prevents a user from editing productId (or the CTA flags) in the URL to load
// a product they did not select. Returns true if the URL was rewritten.
// SECURITY: this is obfuscation only — the cart/checkout APIs must still enforce
// entitlement server-side; do not treat URL cleanup as an access control.
const captureAndStripCheckoutParams = (checkoutAppIdRef, checkoutCtaFlagsRef) => {
  try {
    const relPath = stripLocalePrefix(toShellRelativePath(window.location.pathname));
    if (!relPath.startsWith('/checkout')) return false;

    const params = new URLSearchParams(window.location.search);
    const productId = params.get('productId');
    const editionId = params.get('editionId');
    const hasCheckoutParams = productId
      || editionId
      || params.has('type')
      || params.has('canRouteToV2')
      || params.has('shoppingCart');
    if (!hasCheckoutParams) return false;

    if (productId && !checkoutAppIdRef.current) {
      checkoutAppIdRef.current = productId;
    }
    checkoutCtaFlagsRef.current = {
      editionId: editionId || null,
      ctaType: params.get('type') || null,
      canRouteToV2: params.get('canRouteToV2') === 'true',
      shoppingCart: params.get('shoppingCart') === 'true',
    };
    // Persist so a later remount (refs reset + URL stripped) can still resolve it.
    persistCheckoutContext({
      appId: checkoutAppIdRef.current || null,
      ...checkoutCtaFlagsRef.current,
    });

    // Drop the query string, keep the path (and hash) so the address bar reads
    // e.g. /en-US/checkout with no editable product context.
    const cleanUrl = `${window.location.pathname}${window.location.hash || ''}`;
    window.history.replaceState(window.history.state, '', cleanUrl);
    return true;
  } catch (e) {
    console.error('[AppShell] captureAndStripCheckoutParams error', e);
    return false;
  }
};
const isHeadlessRoutePath = (pathname = '/') => {
  const candidate = normalizeRoutePath(
    stripLocalePrefix(toShellRelativePath(pathname))
  );

  return Object.keys(APP_ROUTES).some((base) => {
    const routeBase = normalizeRoutePath(base);

    if (routeBase === '/myapps') {
      return CONFIG.ENABLE_HEADLESS_MYAPPS
        && (candidate === routeBase || candidate.startsWith(`${routeBase}/`));
    }

    if (routeBase === '/checkout') {
      return CONFIG.ENABLE_HEADLESS_CHECKOUT
        && (candidate === routeBase || candidate.startsWith(`${routeBase}/`));
    }

    // Gate /apps behind the app-details flag so, when headless app-details is
    // off, navigation falls through to a full-page legacy load instead of a
    // single-spa route with no registered MFE (which left the URL changed but
    // the page blank).
    if (routeBase === '/apps') {
      return CONFIG.ENABLE_HEADLESS_APPDETAILS
        && (candidate === routeBase || candidate.startsWith(`${routeBase}/`));
    }

    if (routeBase === '/pages/solutions/api-central') {
      return CONFIG.ENABLE_HEADLESS_APIC
        && (candidate === routeBase || candidate.startsWith(`${routeBase}/`));
    }
    return routeBase === '/'
      ? candidate === '/'
      : candidate === routeBase || candidate.startsWith(`${routeBase}/`);
  });
};

const safeOriginFromConfig = (urlFromConfig, fallbackOrigin = window.location.origin) => {
  try {
    return urlFromConfig ? new URL(urlFromConfig).origin : fallbackOrigin;
  } catch {
    return fallbackOrigin;
  }
};

const getPathParts = (path) => {
  try {
    const parsed = new URL(path || '/', window.location.origin);
    return {
      pathname: parsed.pathname,
      search: parsed.search,
      hash: parsed.hash
    };
  } catch {
    return {
      pathname: path.split(/[?#]/)[0] || '/',
      search: path.includes('?') ? `?${path.split('?')[1].split('#')[0]}` : '',
      hash: path.includes('#') ? `#${path.split('#')[1]}` : ''
    };
  }
};

const buildLegacyAbsoluteTarget = (legacyBaseUrl, pathWithQueryAndHash) => {
  if (!legacyBaseUrl) return '';
  return `${trimTrailingSlashes(legacyBaseUrl)}/${removeLeadingSlashes(pathWithQueryAndHash || '')}`;
};

const buildLegacySessionBootstrapUrl = (handoffOrigin, continueTarget) => {
  return `${handoffOrigin}/login?continue=${encodeURIComponent(continueTarget)}`;
};

const buildNginxLogoutContinueUrl = (handoffOrigin, continueTarget) => {
  return `${handoffOrigin}/logout?continue=${encodeURIComponent(continueTarget)}`;
};

// Create smart navigation wrapper
const smartNavigate = (path, authOverride) => {
  // print stack trace for debugging
  // console.trace();
  const safePath = path || '';
  const isAuthenticated = typeof authOverride === 'boolean'
    ? authOverride
    : !!window.__AUTH_STATE__?.authenticated;

  console.log('[smartNavigate] requested path:', safePath, 'authOverride:', authOverride);

  if (ABSOLUTE_HTTP_URL_REGEX.test(safePath)) {
    // If we were given a fully-qualified legacy URL that actually targets
    // a headless (shell) route, rewrite it to the shell URL so we stay in
    // the shell even when shell/legacy are on different domains.
    try {
      const requestedUrl = new URL(safePath);
      const legacyBase = CONFIG.LEGACY_APP_URL ? new URL(CONFIG.LEGACY_APP_URL) : null;
      const shellBase = CONFIG.SHELL_URL ? new URL(CONFIG.SHELL_URL) : null;

      console.log('[smartNavigate] processing absolute URL:', requestedUrl.href, {
        requestedUrl: requestedUrl.origin,
        legacyBase: legacyBase ? legacyBase.origin : null,
        shellBase: shellBase ? shellBase.origin : null
      });
      const legacyBasePath = legacyBase ? trimTrailingSlashes(legacyBase.pathname) : '';

      // Keep the full pathname, but drop configured legacy base-path prefix if present.
      let relativePathname = requestedUrl.pathname;
      console.log('[smartNavigate] initial relative pathname:', relativePathname);
      if (legacyBasePath && relativePathname.startsWith(legacyBasePath)) {
        console.log('[smartNavigate] stripping legacy base path:', legacyBasePath);
        relativePathname = relativePathname.slice(legacyBasePath.length) || '/';
      }
      if (!relativePathname.startsWith('/')) relativePathname = `/${relativePathname}`;

      const normalizedPathname = normalizePath(relativePathname);
      const shellRelativePath = toShellRelativePath(normalizedPathname);
      const routePathname = stripLocalePrefix(shellRelativePath);
      const normalizedRoutePathname = normalizeRoutePath(routePathname);
      const isHeadlessRoute = isHeadlessRoutePath(normalizedPathname);
      const isLegacyLoginRoute = LEGACY_LOGIN_PATH_REGEX.test(routePathname);
      const isLegacyLogoutRoute = LEGACY_LOGOUT_PATH_REGEX.test(routePathname);
      const isLegacySessionBridgeRoute = (isLegacyLoginRoute || isLegacyLogoutRoute)
        && requestedUrl.searchParams.has('continue');
      const pathWithQueryAndHash = `${normalizedPathname}${requestedUrl.search}${requestedUrl.hash}`;
      let storeOrigin = null;
      try {
        const switchToStore = store.getState()?.app?.locale;
        if (switchToStore) {
          const storeBase = countryNavigateUrl({ localeValue: switchToStore });
          storeOrigin = storeBase ? new URL(storeBase).origin : null;
        }
      } catch (e) { 
        console.warn('[smartNavigate] failed to resolve store origin from config/state', e);
      }

      const shellOrigin = (shellBase ? shellBase.origin : safeOriginFromConfig(CONFIG.SHELL_URL, window.location.origin));
      if (storeOrigin && requestedUrl.origin === storeOrigin && requestedUrl.origin !== shellOrigin && !isLegacySessionBridgeRoute) {
        const storeTargetUrl = requestedUrl.href;
        console.log('[smartNavigate] locale store route (cross-origin)', { requested: safePath, storeTargetUrl, storeOrigin, shellOrigin, isAuthenticated });
        window.location.href = storeTargetUrl;
        return;
      }
      // If the requested absolute URL is for an external origin (not the configured
      // legacy host), don't canonicalize it to the configured legacy host. Preserve
      // the original absolute URL for navigation (e.g., external docs sites).
      if (legacyBase && requestedUrl.origin !== legacyBase.origin) {
        console.log('[smartNavigate] absolute external origin — not canonicalizing to LEGACY_APP_URL', { requested: safePath, requestedOrigin: requestedUrl.origin, legacyOrigin: legacyBase.origin });
        window.location.href = requestedUrl.href;
        return;
      }
      const legacyBaseUrl = trimTrailingSlashes(legacyBase ? legacyBase.href : '');
      const legacyOrigin = legacyBase ? legacyBase.origin : safeOriginFromConfig(CONFIG.LEGACY_APP_URL, window.location.origin);
      const canonicalLegacyTarget = buildLegacyAbsoluteTarget(legacyBaseUrl, pathWithQueryAndHash) || requestedUrl.href;

      if (isHeadlessRoute && !isLegacySessionBridgeRoute) {
        const rewrittenUrl = `${shellOrigin}${normalizedPathname}${requestedUrl.search}${requestedUrl.hash}`;
        console.log('[smartNavigate] absolute URL resolved as headless route', {
          requested: safePath,
          normalizedRoutePathname,
          rewritten: rewrittenUrl
        });
        navigateToUrl(rewrittenUrl);
        return;
      }

      // Legacy routes are always canonicalized to configured legacy host (apps.adp.com in PRE).
      const continuePath = pathWithQueryAndHash;

      const targetUrl = isAuthenticated && isLegacyLogoutRoute
        ? buildNginxLogoutContinueUrl(legacyOrigin, '/home')
        : isAuthenticated && !isLegacyLoginRoute
        ? buildLegacySessionBootstrapUrl(legacyOrigin, continuePath)
        : canonicalLegacyTarget;
        
      console.log('[smartNavigate] absolute URL resolved as legacy route', {
        requested: safePath,
        canonicalLegacyTarget,
        targetUrl,
        isAuthenticated
      });
      window.location.href = targetUrl;
      return;
    } catch (e) {
      console.warn('[smartNavigate] failed to parse/inspect absolute URL, falling back to hard navigation', e);
    }

    console.debug('[smartNavigate] absolute URL -> hard navigation', { url: safePath });
    window.location.href = safePath;
    return;
  }

  // Parse and normalize paths so:
  // - APP_ROUTES keys like '/myapps' match when callers pass '/myapps/'
  // - callers can pass shell-base-prefixed paths like '/cmp/myapps' and still match '/myapps'
  // - query/hash are preserved for the final navigation URL
  const { pathname: rawPathname, search, hash } = getPathParts(safePath);

  const shellRelativePathname = toShellRelativePath(rawPathname);
  const routePathname = stripLocalePrefix(shellRelativePathname);
  const normalizedRoutePathname = normalizeRoutePath(routePathname);
  const isHeadlessRoute = isHeadlessRoutePath(shellRelativePathname);

  console.debug('[smartNavigate] route classification', {
    requested: safePath,
    headlessKey: normalizedRoutePathname,
    rawHeadlessKey: rawPathname,
    routePathname,
    isHeadlessRoute,
    isAuthenticated
  });

  // Internal headless route: use single-spa navigation
  if (isHeadlessRoute) {
    const basePath = ROUTER_BASENAME;
    const currentLocale = store.getState()?.app?.locale || 'en-US';
    const hasLocalePrefix = /^\/[a-z]{2}(?:-[a-z]{2})?(?=\/|$)/i.test(shellRelativePathname);
    // login/logout keep bare paths so nginx `location = /login` logic
    // (which gates ?continue= params to legacy) is not bypassed.
    const isAuthRoute = LEGACY_LOGIN_PATH_REGEX.test(shellRelativePathname) || LEGACY_LOGOUT_PATH_REGEX.test(shellRelativePathname);
    // Canonicalize root '/' to '/home' so navigation always lands on the
    // explicit home route rather than '/en-US/' which servers may not handle.
    const canonicalPathname = shellRelativePathname === '/' ? '/home' : shellRelativePathname;
    const localizedPathname = hasLocalePrefix || isAuthRoute
      ? canonicalPathname
      : `/${currentLocale}${canonicalPathname}`.replace(/\/{2,}/g, '/');
    const targetPathname = `${basePath}${localizedPathname}`.replace(/\/{2,}/g, '/') || '/';
    const shellOrigin = safeOriginFromConfig(CONFIG.SHELL_URL, window.location.origin);
    const fullPath = `${shellOrigin}${targetPathname}${search}${hash}`;

    console.log('[smartNavigate] internal headless route navigation via single-spa', {
      requested: safePath,
      fullPath
    });
    navigateToUrl(fullPath);
    return;
  }

  // Legacy app navigation
  const legacyBase = CONFIG.LEGACY_APP_URL;
  const normalizedLegacyPath = normalizePath(rawPathname || '/');
  const legacyPathWithQuery = `${normalizedLegacyPath}${search}${hash}`;
  const legacyBaseUrl = trimTrailingSlashes(legacyBase || '');
  const legacyOrigin = safeOriginFromConfig(CONFIG.LEGACY_APP_URL, window.location.origin);
  const isLegacyLoginRoute = LEGACY_LOGIN_PATH_REGEX.test(routePathname);
  const isLegacyLogoutRoute = LEGACY_LOGOUT_PATH_REGEX.test(routePathname);
  // const shellOrigin = safeOriginFromConfig(CONFIG.SHELL_URL, window.location.origin);

  let targetUrl = buildLegacyAbsoluteTarget(legacyBaseUrl, legacyPathWithQuery);

  // For authenticated users, force legacy handoff through legacy login with a continue URL.
  // Legacy login creates the JSESSIONID needed before serving legacy pages.
  if (legacyBaseUrl && isAuthenticated && !isLegacyLoginRoute && !isLegacyLogoutRoute) {
    targetUrl = buildLegacySessionBootstrapUrl(legacyOrigin, legacyPathWithQuery);
  }

  if (legacyBaseUrl && isAuthenticated && isLegacyLogoutRoute) {
    targetUrl = buildNginxLogoutContinueUrl(legacyOrigin, '/home');
  }

  if (!targetUrl || !legacyBase) {
    console.warn('[smartNavigate] targetUrl is falsy (likely unauthenticated or misconfigured). Navigation aborted.', {
      isAuthenticated,
      requestedPath: safePath
    });
    return;
  }

  try {
    console.debug('[smartNavigate] legacy navigation -> hard navigation', {
      targetUrl,
      isAuthenticated,
      isLegacyLoginRoute,
      legacyPathWithQuery
    });
    window.location.href = targetUrl;
  } catch (err) {
    console.error('[smartNavigate] window.location.href assignment failed', err);
  }
};

window.singleSpa = Object.assign({}, window.singleSpa || {}, {
  navigateToUrl: (path, authOverride) => smartNavigate(path, authOverride),
  _originalNavigateToUrl: navigateToUrl
});

const MFEContainer = ({ children, ...props }) => (
  <Suspense
    fallback={
      <LoadingState>
        <LoadingContent>
          <SdfBusyIndicator />
        </LoadingContent>
      </LoadingState>
    }
  >
    <MainContainer {...props}>
      {children}
    </MainContainer>
  </Suspense>
);

export function broadcastAuthState({ authenticated, loading, authChecked, userContext, locale }) {
  window.__AUTH_STATE__ = {
    authenticated: !!authenticated,
    loading: !!loading,
    authChecked: !!authChecked,
    userContext: userContext || null,
    timestamp: Date.now(),
    locale: locale || 'en-US'
  };
  window.dispatchEvent(new CustomEvent('auth-state-change', { detail: window.__AUTH_STATE__ }));
}

function AppShell() {
  const [singleSpaStarted, setSingleSpaStarted] = useState(false);
  const [error, setError] = useState(null);
  const [isContactOpen, setIsContactOpen] = useState(false);
  const [leadAppInfo, setLeadAppInfo] = useState(null);
  const checkoutAppIdRef = useRef(null);
  const checkoutCtaFlagsRef = useRef(null);

  // Capture legacy checkout CTA params (productId/editionId/type/canRouteToV2/shoppingCart)
  // into refs and strip them from the URL before first paint, so the product
  // context is never left editable in the address bar.
  useLayoutEffect(() => {
    captureAndStripCheckoutParams(checkoutAppIdRef, checkoutCtaFlagsRef);
  }, []);

  const { locale = '' } = useSelector(state => state.app) || {};
  const dispatch = useDispatch();
  const userContext = useSelector(state => state.userContext);
  const { gaClientId } = useSelector(state => state.app);
  const { authenticated, loading: authLoading, authChecked, sessionCode } = useAuthentication(
    {},
    CONFIG.POST_LOGIN_REDIRECT_URI
  );

  const handleContactUsClick = useCallback((appId, appName) => {
    setLeadAppInfo(appId ? { applicationId: appId, applicationName: appName } : null);
    setIsContactOpen(true);
  }, []);

  const handleCheckout = useCallback((appId, editionId) => {
    // Capture appId (and optional editionId) from app-details before navigating away
    checkoutAppIdRef.current = appId || null;
    checkoutCtaFlagsRef.current = { ...(checkoutCtaFlagsRef.current || {}), editionId: editionId || null };
    persistCheckoutContext({ appId: appId || null, editionId: editionId || null, ctaType: null, canRouteToV2: false, shoppingCart: false });
    smartNavigate(`/checkout/${appId}`);
  }, []);

  const isMobileSearchOpen = useSelector((state) => state?.app?.isMobileSearchOpen);
  useEffect(() => {
    // React to SPA navigation without using `window.location.pathname` as a dependency.
    // Shim history.pushState/replaceState to dispatch a `locationchange` event so
    // single-page navigations trigger the handler, and also listen for `popstate`.
    const handleLocationChange = () => {
      try {
        updatePageMeta(i18n);
        const localeMatch = window.location.pathname.match(/\/([a-z]{2}-[A-Z]{2})(?=\/|$)/);
        const urlLocale = localeMatch ? localeMatch[1] : 'en-US';
        if (
          window.location.pathname === process.env.REACT_APP_INTL_STORE &&
          urlLocale === 'en-US' &&
          i18n.options.defaultNS !== 'mcp'
        ) {
          i18n.options.defaultNS = 'mcp';
          i18n.loadNamespaces('mcp');
          console.log('[AppShell] Changed i18n namespace to mcp');
        }

        if (i18n.language !== urlLocale) {
          console.log('[AppShell] changeLanguage (from URL) ->', urlLocale);
          i18n.changeLanguage(urlLocale).catch(e => console.error('[AppShell] changeLanguage error', e));
        }
        dispatch(setCountryAndLocale({ locale: urlLocale }));
      } catch (e) {
        console.error('[AppShell] location change handler error', e);
      }
    };

    // Run once on mount to pick up initial location
    handleLocationChange();

    // router.subscribe fires on every navigation — stable API, no monkey-patching.
    // Covers pushState, replaceState, and popstate (back/forward), so no need
    // for a separate popstate listener which would cause double-firing on back.
    const unsubscribe = router.subscribe(() => handleLocationChange());

    const handlePageShow = (e) => { if (e.persisted) handleLocationChange(); };
    window.addEventListener('pageshow', handlePageShow);

    return () => {
      unsubscribe();
      window.removeEventListener('pageshow', handlePageShow);
    };
  }, [dispatch]);

  useEffect(() => {
    // Only fetch if authenticated, auth checked, and we don't already have user info
    // Dispatch when there is no userContext OR when userContext is not loading and has no userInfo
    if (
      authenticated &&
      authChecked &&
      (!userContext || (!userContext.isLoading && !userContext.userInfo))
    ) {
      dispatch(getUserContext());

      const returnUrl = new URLSearchParams(window.location.search).get('returnUrl') || null;
      if (returnUrl) {
        console.log('Auth complete with returnUrl, navigating to:', returnUrl);
        smartNavigate(returnUrl, true);
      }
    }
  }, [authenticated, authChecked, dispatch]);

  // Initialize or update Pendo when user context is available
  useEffect(() => {
    loadPendoTracking({ authenticated, userContext, gaClientId });
    return () => {
      unloadPendoTracking();
    };
  }, [authenticated, userContext, gaClientId]);
    // Set window.customDimensions for analytics - shows "empty" for logged out, actual values for logged in
  useEffect(() => {
    setCustomDimensions();
  }, [userContext]);

  // const userContextLoaded = !!userContext && !isLoading && !!userContext.userInfo;

  // Global state updates and event handling
  useEffect(() => {
    broadcastAuthState({
      authenticated,
      loading: authLoading,
      authChecked,
      userContext,
      locale
    });
  }, [authenticated, authLoading, authChecked, userContext]);

  useEffect(() => {
    loadGTM();
    ga4Init();
    // initAutoGA4();
    loadInteractionStudio();
    loadDemandBase();
    const handleLoginRequest = (event) => {
      console.log('[AppShell] auth-request-login event received:', event);
      const returnUrl = event.detail?.returnUrl || window.location.pathname + window.location.search;
      const plru = event.detail?.postLoginRedirectUriOverride;
      const qs = new URLSearchParams({ returnUrl });
      if (plru) qs.set('plru', plru);
      window.singleSpa.navigateToUrl(`/login?${qs.toString()}`);
    };

    const handleLogoutRequest = () => {
      console.log('[AppShell] auth-request-logout event received');
      window.singleSpa.navigateToUrl('/logout');
    };

    window.addEventListener('auth-request-login', handleLoginRequest);
    window.addEventListener('auth-request-logout', handleLogoutRequest);
    return () => {
      window.removeEventListener('auth-request-login', handleLoginRequest);
      window.removeEventListener('auth-request-logout', handleLogoutRequest);
    };
  }, []);

  useEffect(() => {
    const initializeSingleSpa = async () => {
      try {
        console.log('Initializing Single-SPA from React Root...', authenticated, userContext);
        if (!singleSpaStartedGlobally) {
          addErrorHandler((err) => {
            console.error('Single-SPA Error:', err);
            setError(err.message);
          });
        }
        if (!singleSpaStartedGlobally) {
          await cleanupExistingApps();
        }
        await registerApplications(authenticated, authChecked, userContext, dispatch, locale, gaClientId, handleContactUsClick, handleCheckout, checkoutAppIdRef, checkoutCtaFlagsRef);
        if (!singleSpaStartedGlobally && getAppNames().length > 0) {
          console.log('Starting Single-SPA...');
          start({
            urlRerouteOnly: true
          });
          singleSpaStartedGlobally = true;
          console.log('Single-SPA started successfully');
        }
        setSingleSpaStarted(true);
      } catch (error) {
        console.error('Failed to initialize Single-SPA:', error);
        setError(`Failed to initialize the application: ${error.message}`);
      }
    };
    if (authChecked && !singleSpaStarted) {
      initializeSingleSpa();
    }
  }, [authenticated, authChecked, singleSpaStarted, userContext, handleContactUsClick, handleCheckout, dispatch]);

  if (authLoading || !authChecked) {
    return (
      <MuiThemeProvider>
        <RootContainer>
          <SharedHeader authenticated={null} navigate={smartNavigate} sessionCode={sessionCode} />
          <LoadingState>
            <LoadingContent>
              <SdfBusyIndicator />
            </LoadingContent>
          </LoadingState>
          <SharedFooter navigate={smartNavigate} onContactUsClick={handleContactUsClick} />
        </RootContainer>
      </MuiThemeProvider>
    );
  }
  if (error) {
    return (
      <MuiThemeProvider>
        <RootContainer>
          <SharedHeader authenticated={authenticated} navigate={smartNavigate} sessionCode={sessionCode} />
          <ErrorState>
            <ErrorContent>
              <ErrorTitle>Error Loading Application</ErrorTitle>
              <ErrorMessage>{error}</ErrorMessage>
              <StyledReloadButton
                onClick={() => window.location.reload()}
              >
                Reload Page
              </StyledReloadButton>
            </ErrorContent>
          </ErrorState>
          <SharedFooter navigate={smartNavigate} onContactUsClick={handleContactUsClick} />
        </RootContainer>
      </MuiThemeProvider>
    );
  }
  console.log('config app shell', { SHELL_BASE_PATH, ROUTER_BASENAME });
  return (
    <MuiThemeProvider>
      <RootContainer>
        <SharedHeader authenticated={authenticated} navigate={smartNavigate} userContext={userContext} sessionCode={sessionCode} isContactOpen={isContactOpen} setIsContactOpen={setIsContactOpen} onContactUsClick={handleContactUsClick} />
        {isContactOpen && <ContactUs onClose={() => setIsContactOpen(false)} navigate={smartNavigate} userInfo={userContext?.userInfo} locale={locale} {...(leadAppInfo ? { ...leadAppInfo } : {})} />}
        <MFEContainer id="single-spa-application-container" aria-label="Main application content">
          <AppContent id="headless-app-content" $searchOpen={isMobileSearchOpen} >
            <div id="app"></div>
            <div id="smart-search-root"></div>
            <div id="enterprise-search-root" className={`enterprise-search-container ${!authenticated ? "nonLoggedSearch": "loggedSearch"}`}></div>
            <div id="contact-us-root"></div>
            <div id="my-apps-root"></div>
            <div id="app-details-root"></div>
            <div id="homepage-root"></div>
            <div id="employee-wellness-root"></div>
            <div id="auth-route-root"></div>
            <div id="checkout-root" />
             <div id="apic-root"></div>
          </AppContent>
        </MFEContainer>
        <SharedFooter navigate={smartNavigate} onContactUsClick={handleContactUsClick} />
      </RootContainer>
    </MuiThemeProvider>
  );
}

// Thin wrapper rendered by the router — just mounts AppShell inside RouterProvider context.
function AppShellContent() {
  return <AppShell />;
}

// Router created after AppShellContent is defined so the JSX reference is valid.
// Stable API — no unstable_ prefix, no external history package needed.
const router = createBrowserRouter(
  [{ path: '*', element: <AppShellContent /> }],
  { basename: ROUTER_BASENAME || undefined }
);

async function registerApplications(authenticated, authChecked, userContext, dispatch, locale, gaClientId, onContactUsClick, onCheckoutClick, checkoutAppIdRef, checkoutCtaFlagsRef) {
  console.log('Registering content MFEs...');

  const baseProps = {
    apiBaseUrl: CONFIG.API_BASE_URL,
    environment: CONFIG.NODE_ENV,
    authenticated,
    authChecked,
    userContext,
    locale,
    dispatch,
    navigate: smartNavigate,
    navigateToUrl: smartNavigate,
    smartNavigate,
    queryClient: window.__SHARED_QUERY_CLIENT__
  };
  const existingApps = getAppNames();

  const appsToRegister = [
    // Homepage as MFE (replaces the old embedded home-app)
    {
      name: 'homepage-mfe',
      loader: () => import('../components/remotes/homePageApp'),
      activeWhen: (location) => {
        const path = stripLocalePrefix(toShellRelativePath(location.pathname));
        return path === '/' || getActiveRoutes(location, 'home');
      },
      customProps: { ...baseProps }
    },
    // Old embedded home-app (commented out - now using homepage-mfe above)
    // {
    //   name: 'home-app',
    //   loader: () => import('../apps/HomeApp'),
    //   activeWhen: (location) => {
    //     const path = toShellRelativePath(location.pathname);
    //     return path === '/' || getActiveRoutes(location, 'home');
    //   },
    //   customProps: { ...baseProps }
    // },
    {
      name: 'enterprise-search-mfe',
      loader: () => import('../components/remotes/enterpriseSearchApp'),
      activeWhen: (location) =>
        getActiveRoutes(location, 'search') ||
        getActiveRoutes(location, 'listing') ||
        getActiveRoutes(location, 'compare'),
      customProps: {
        ...baseProps,
        documentType: "MKP",
        gaClientId,
        environment: CONFIG.REACT_APP_STAGE || CONFIG.NODE_ENV,
      },
    },
    {
      name: 'app-details-mfe',
      loader: () => import('../components/remotes/appDetailsApp'),
      activeWhen: (location) => CONFIG.ENABLE_HEADLESS_APPDETAILS
        && getActiveRoutes(location, 'apps'),
      customProps: {
        ...baseProps,
        onContactUsClick,
        onCheckoutClick,
      }
    },
    {
      name: 'employee-wellness-mfe',
      loader: () => import('../components/remotes/employeeWellnessApp'),
      activeWhen: (location) => getActiveRoutes(location, 'resources-insights/employee-experience-solutions'),
      customProps: {
        ...baseProps
      }
    },
    {
      name: 'auth-login',
      loader: () => Promise.resolve({
        bootstrap: () => Promise.resolve(),
        mount: async () => {
          const container = ensureRootElement('auth-route-root');
          if (!window.__loginRoot) {
            const { createRoot } = await import('react-dom/client');
            window.__loginRoot = createRoot(container);
          }
          console.log(`login called ${window.location.pathname}${window.location.search}`);
          window.__loginRoot.render(<LoginPageComponent />);
        },
        unmount: async () => {
          if (window.__loginRoot) {
            window.__loginRoot.unmount();
            window.__loginRoot = null;
          }
          const container = document.getElementById('auth-route-root');
          if (container) container.innerHTML = '';
        }
      }),
      activeWhen: (location) => getActiveRoutes(location, 'login'),
      customProps: { ...baseProps }
    },
    {
      name: 'my-apps-mfe',
      loader: () => import('../components/remotes/myAppsApp'),
      activeWhen: (location) => CONFIG.ENABLE_HEADLESS_MYAPPS
        && getActiveRoutes(location, 'myapps'),
      customProps: {
        ...baseProps
      }
    },
    {
      name: 'auth-logout',
      loader: () => Promise.resolve({
        bootstrap: () => Promise.resolve(),
        mount: async () => {
          const container = ensureRootElement('auth-route-root');
          if (!window.__logoutRoot) {
            const { createRoot } = await import('react-dom/client');
            window.__logoutRoot = createRoot(container);
          }
          window.__logoutRoot.render(<LogoutPageComponent />);
        },
        unmount: async () => {
          if (window.__logoutRoot) {
            window.__logoutRoot.unmount();
            window.__logoutRoot = null;
          }
          const container = document.getElementById('auth-route-root');
          if (container) container.innerHTML = '';
        }
      }),
      activeWhen: (location) => getActiveRoutes(location, 'logout'),
    },
    {
      name: 'checkout-mfe',
      loader: () => import('../components/remotes/checkoutApp'),
      activeWhen: (location) => CONFIG.ENABLE_HEADLESS_CHECKOUT
        && getActiveRoutes(location, 'checkout'),
      customProps: () => ({
        ...baseProps,
        // Prefer appId passed from app-details (/apps/:id); fall back to the
        // productId query param (legacy CTA redirect) or a /checkout/:appId path segment.
        appId: resolveCheckoutAppId(checkoutAppIdRef),
        // editionId + CTA flags (type/canRouteToV2/shoppingCart) from the legacy redirect.
        ...resolveCheckoutCtaFlags(checkoutCtaFlagsRef),
      })
    },
    {
      name: 'apic-mfe',
      loader: () => import('../components/remotes/apicApp'),
      activeWhen: (location) => CONFIG.ENABLE_HEADLESS_APIC
        && getActiveRoutes(location, 'pages/solutions/api-central'),
      customProps: { ...baseProps }
    },
  ];

  for (const appConfig of appsToRegister) {
    if (!existingApps.includes(appConfig.name)) {
      console.log(`Registering ${appConfig.name}`);
      registerApplication({
        name: appConfig.name,
        app: appConfig.loader,
        activeWhen: appConfig.activeWhen,
        customProps: appConfig.customProps
      });
    } else {
      console.log(`Skipping ${appConfig.name} - already registered`);
    }
  }
  console.log('Final registered apps:', getAppNames());
}

async function cleanupExistingApps() {
  const existingApps = getAppNames();
  console.log('Cleaning up existing apps:', existingApps);
  if (singleSpaStartedGlobally) {
    console.log('Skipping cleanup - Single-SPA already started');
    return;
  }
  const appsToCleanup = [
    'homepage-mfe',  // Homepage as MFE (replaces old "home-app")
    'smart-search-mfe',
    'contact-us-mfe',
    'enterprise-search-mfe',
    'checkout-mfe',
    'my-apps-mfe',
    'app-details-mfe',
    'employee-wellness-mfe',
    'apic-mfe',
    // 'legacy-appstore'
  ];
  for (const appName of appsToCleanup) {
    if (existingApps.includes(appName)) {
      try {
        console.log(`Unregistering ${appName}`);
        await unregisterApplication(appName);
      } catch (error) {
        console.warn(`Failed to unregister ${appName}:`, error);
      }
    }
  }
}

// Ensure a root element exists in the DOM for MFEs to mount into.
function ensureRootElement(id) {
  if (typeof document === 'undefined') return null;
  let el = document.getElementById(id);
  if (!el) {
    const parent = document.getElementById('headless-app-content')
      || document.getElementById('single-spa-application-container')
      || document.body;
    el = document.createElement('div');
    el.id = id;
    parent.appendChild(el);
    console.warn(`[AppShell] Created missing root element #${id}`);
  }
  return el;
}

import PropTypes from 'prop-types';
import { ga4Init, loadGTM, setCustomDimensions } from '../analytics/ga4';
// import { initAutoGA4 } from '@headless-shell/homepage';
// import { env } from 'process';
// import { trace } from 'console';

MFEContainer.propTypes = {
  children: PropTypes.node
};

// Root export — RouterProvider owns the router context; AppShellContent → AppShell renders inside it.
function AppShellRoot() {
  return <RouterProvider router={router} />;
}

export default AppShellRoot;